import type { QueryClient } from "@tanstack/react-query";

import type { PlatformHealthResponse } from "../../shared/schemas";
import { platformVersionQuery } from "../api/queries";

export class LoadedDeploymentVersionStore {
  readonly #queryClient: QueryClient;
  readonly #listeners = new Set<() => void>();
  #loadedVersion: string | null;
  #unsubscribe: (() => void) | null = null;

  constructor(queryClient: QueryClient) {
    this.#queryClient = queryClient;
    this.#loadedVersion = this.#readVersion();
  }

  readonly getSnapshot = (): string | null => this.#loadedVersion;

  readonly subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    if (!this.#unsubscribe) {
      this.#unsubscribe = this.#queryClient.getQueryCache().subscribe(() => {
        this.#captureInitialVersion();
      });
      this.#captureInitialVersion();
    }
    return () => {
      this.#listeners.delete(listener);
      if (this.#listeners.size === 0) {
        this.#unsubscribe?.();
        this.#unsubscribe = null;
      }
    };
  };

  #readVersion(): string | null {
    return (
      this.#queryClient.getQueryData<PlatformHealthResponse>(platformVersionQuery.queryKey)
        ?.deploymentVersion ?? null
    );
  }

  #captureInitialVersion(): void {
    if (this.#loadedVersion !== null) return;
    const observedVersion = this.#readVersion();
    if (!observedVersion) return;
    this.#loadedVersion = observedVersion;
    for (const listener of this.#listeners) listener();
  }
}
